<script setup lang="ts">
import { computed, onMounted, ref, type Component } from 'vue'
import { toast } from 'vue-sonner'
import * as Sentry from '@sentry/nuxt'
import { Button, type ButtonVariants } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip'
import { UserPlus, UserCheck, Clock } from 'lucide-vue-next'
import { useFriendsStore } from '~/stores/friends'
import { FriendshipStatus } from '~/types/friends'

// The root is a Tooltip wrapper (renderless), so fallthrough attrs like `class` are bound to the
// button/spinner by hand.
defineOptions({ inheritAttrs: false })

const props = defineProps<{
  userId: string
  username: string
  // Forwarded to the button only — as a plain fallthrough attr it would also land on the loading
  // Spinner (a lucide icon, where `size` means pixels).
  size?: ButtonVariants["size"]
  // Icon-only round button: the label stays for screen readers, and a tooltip explains the action.
  iconOnly?: boolean
}>()

const friendsStore = useFriendsStore()

const status = ref<FriendshipStatus>(FriendshipStatus.Unknown)
const isLoadingStatus = ref(true)
const isActing = ref(false)

// Every friend action below is keyed by the *other user's* id (`props.userId`), the same way the
// store and SidebarFriendsList call them — the backend's `friend_id` means that, not the
// friendship record id `get_friendship_status` returns. Passing the latter here made accept/
// cancel/remove fail with NotFound ("Friend request not found.").
async function refreshStatus() {
  isLoadingStatus.value = true
  try {
    const response = await friendsStore.getFriendshipStatus(props.userId)
    if (response) status.value = response.status
  } finally {
    isLoadingStatus.value = false
  }
}

onMounted(refreshStatus)

type ButtonVariant = ButtonVariants["variant"]

// One entry per visible state: `label` is the button text, `hint` the icon-only tooltip, which
// says what a click will do (the label alone, e.g. "Заявка отправлена", doesn't).
const action = computed<{ label: string; hint: string; icon: Component; variant: ButtonVariant; run: () => void } | null>(() => {
  switch (status.value) {
    case FriendshipStatus.Accepted:
      return { label: 'Удалить из друзей', hint: 'Удалить из друзей', icon: UserCheck, variant: 'outline', run: handleCancelOrRemove }
    case FriendshipStatus.PendingOutgoing:
      return { label: 'Заявка отправлена', hint: 'Заявка отправлена — нажмите, чтобы отменить', icon: Clock, variant: 'secondary', run: handleCancelOrRemove }
    case FriendshipStatus.PendingIncoming:
      return { label: 'Принять заявку', hint: 'Принять заявку в друзья', icon: UserCheck, variant: 'default', run: handleAccept }
    case FriendshipStatus.Blocked:
      return null
    default:
      return { label: 'Добавить в друзья', hint: 'Добавить в друзья', icon: UserPlus, variant: 'outline', run: handleAdd }
  }
})

async function handleAdd() {
  isActing.value = true
  try {
    const request = await friendsStore.sendFriendRequest(props.username)
    status.value = request.status
    toast.success(request.status === FriendshipStatus.Accepted ? 'Теперь вы друзья' : 'Заявка отправлена')
  } catch (e: any) {
    console.error('Failed to send friend request', e)
    Sentry.captureException(e, { extra: { action: 'send_friend_request_from_profile', userId: props.userId } })
    toast.error(e?.message || 'Не удалось отправить заявку в друзья')
  } finally {
    isActing.value = false
  }
}

async function handleAccept() {
  isActing.value = true
  try {
    await friendsStore.acceptFriendRequest(props.userId)
    status.value = FriendshipStatus.Accepted
    toast.success('Заявка принята')
  } catch (e: any) {
    console.error('Failed to accept friend request', e)
    Sentry.captureException(e, { extra: { action: 'accept_friend_request_from_profile', userId: props.userId } })
    toast.error('Не удалось принять заявку')
  } finally {
    isActing.value = false
  }
}

async function handleCancelOrRemove() {
  isActing.value = true
  try {
    if (status.value === FriendshipStatus.PendingOutgoing) {
      await friendsStore.cancelFriendRequest(props.userId)
      status.value = FriendshipStatus.Cancelled
      toast.success('Заявка отменена')
    } else if (status.value === FriendshipStatus.Accepted) {
      await friendsStore.removeFriend(props.userId)
      status.value = FriendshipStatus.Removed
      toast.success('Пользователь удалён из друзей')
    }
  } catch (e: any) {
    console.error('Failed to cancel/remove friend', e)
    Sentry.captureException(e, { extra: { action: 'cancel_or_remove_friend_from_profile', userId: props.userId } })
    toast.error('Не удалось выполнить действие')
  } finally {
    isActing.value = false
  }
}
</script>

<template>
  <!-- Icon-only: hold the button's exact footprint while the status loads — a bare 16px spinner
       swapping for a 36px button shifted everything next to it once the status arrived. -->
  <Button v-if="isLoadingStatus && iconOnly" v-bind="$attrs" size="icon" variant="outline" disabled aria-label="Загрузка статуса дружбы">
    <Spinner />
  </Button>

  <Spinner v-else-if="isLoadingStatus" v-bind="$attrs" class="size-4" />

  <TooltipProvider v-else-if="action">
    <Tooltip :disabled="!iconOnly">
      <TooltipTrigger as-child>
        <Button v-bind="$attrs" :variant="action.variant" :size="iconOnly ? 'icon' : size" :disabled="isActing" @click="action.run">
          <Spinner v-if="isActing" />
          <component :is="action.icon" v-else class="size-4" />
          <span :class="iconOnly && 'sr-only'">{{ action.label }}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{{ action.hint }}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
